<!-- EntityDetail.vue -->
<template>
  <div id="entity-detail" class="component stk-p-8">
    <LoaderWithText
      v-if="loading"
      class="stk-py-2_5"
    >
      Fetching Data…
    </LoaderWithText>

    <template v-else>
      <Level id="entity-header" class="stk-mb-6">
        <LevelLeft>
          <Title size="7">
            {{ entity?.name || '' }}
          </Title>
          <Subtitle v-if="entity.email" class="email" size="5">
            <a :href="`mailto:${entity.email}`">
              {{ entity.email }}
            </a>
          </Subtitle>
        </LevelLeft>

        <LevelRight>
          <Buttons size="3_5">
            <!-- Client Portal Actions -->
            <Button
              v-if="advisorStore.featureFlags.includes('Dossier') && advisorStore.featureFlags.includes('ClientPortal')"
              class="portal-status-button"
              icon-before="fas fa-external-link-alt"
              @click="redirectToClientPortal"
            >
              View Client Portal: <Badge
                :status="portalStatusBadge.status"
              >
                {{ portalStatusBadge.text }}
              </Badge>
            </Button>
            

            <Button icon-before="fas fa-edit" @click="openEditModal">
              <span>Edit</span>
            </Button>

            <Button
              v-if="advisorStore.hasFeature('FAFN Direct Indexing')"
              icon-before="fas fa-network-wired"
              :href="`/advisor-dashboard/direct-indexing/${entity.linked_entity_external_id}`"
              target="_blank"
              title="Values-Aligned Direct Index Solution"
            >
              <span>VADIS</span>
            </Button>

          </Buttons>
        </LevelRight>
      </Level>

      <div id="entity-scores" class="tw-flex tw-justify-between tw-items-start tw-mb-4">
        <!-- Left: Risk columns (unchanged) -->
        <div class="tw-flex tw-gap-8">
          <!-- Client Risk -->
          <div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <strong>Client Risk Level:</strong>
              <Badge v-if="clientRiskScore !== null" status="primary">
                {{ `${clientRiskScore} (${ riskMapping(clientRiskScore).level })`}}
              </Badge>
              <span v-else>—</span>
            </div>
            <p class="tw-flex">
              <router-link
                class="is-primary stk-size-3"
                :to="{ name: 'entity-reports' }"
                replace
              >
                {{ clientRiskScore !== null ? 'View / Change' : 'Add / Update' }}
              </router-link>
            </p>
          </div>

          <!-- Portfolio Risk -->
          <div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <strong>Portfolio Risk Score:</strong>

              <!-- spinner while fetching -->
              <span v-if="isFetchingPortfolioRisk">
                <i class="fa fa-spinner fa-spin"></i>
              </span>

              <!-- once fetched, show badge or dash -->
              <Badge v-else-if="portfolioRiskScore !== null" status="primary">
                {{ `${Math.round(portfolioRiskScore)} (${ riskMapping(portfolioRiskScore).level })` }}
              </Badge>
              <span v-else>—</span>
              <span
                v-if="portfolioRiskScore !== null && clientRiskScore !== null && Math.abs(portfolioRiskScore - clientRiskScore) > 10"
                class="tw-text-red-500 tw-cursor-pointer"
                title="The portfolio risk score differs significantly from the client risk level."
              >
                <i class="fas fa-exclamation-circle"></i>
              </span>
            </div>
            <p class="tw-flex">
              <router-link
                v-if="!isFetchingPortfolioRisk && portfolioRiskScore === null"
                replace
                class="is-primary stk-size-3"
                :to="{ name: 'entity-portfolio' }"
              >
                Add holdings
              </router-link>
            </p>
          </div>
        </div>

        <!-- Right: Client Values as its own column -->
        <div>
          <!-- label + up to 4 badges -->
          <div class="tw-flex tw-items-center tw-gap-2">
            <strong>Client Values:</strong>
            <Badge
              v-for="val in truncatedValues"
              :key="val"
              status="success"
            >{{ val }}</Badge>
            <Badge
              v-if="extraValuesCount > 0"
              status="info"
              clickable
              :to="{ name: 'entity-reports' }"
              replace
            >
              +{{ extraValuesCount }} more
            </Badge>
          </div>
          <p class="tw-flex">
            <router-link
              class="is-primary stk-size-3"
              :to="{ name: 'entity-reports' }"
              replace
            >
              Change
            </router-link>
          </p>
        </div>
      </div>

      <!-- Remove the comment - it's causing confusion -->
      <StakeModal v-model="showInviteModal">
        <Stack class="stk-p-4">
          <EntityInviteForm 
            :entity-external-id="entityExternalId" 
            :entity-email="entity?.email"
            @invitation-link-generated="handleInvitationLinkGenerated"
          />
        </Stack>
      </StakeModal>

      <template v-if="hasPermission">
        <Pills id="entity-navigation-links">
          <PillsList>
            <PillItem
              v-for="link in navigationLinks"
              :key="link.to.name"
              :to="link.to"
              :icon="link.icon"
              replace
            >
              {{ link.label }}
            </PillItem>
          </PillsList>
        </Pills>

    

        <router-view
          :entity-external-id="entityExternalId"
          :entity="entity"
          @refetch-entity="refetchEntityAndRisk"
        ></router-view>

        <StakeModal v-model="showEditModal">
          <!-- wrap in a form that prevents normal POST and calls your save method -->
          <form @submit.prevent="saveEntityDetails">
            <Stack class="stk-p-2" style="width: 32rem;" gap="4">
              <Title>Edit Entity</Title>

              <Field>
                <Label>First Name</Label>
                <!-- now Enter here will submit the form -->
                <input
                  class="stk-input"
                  type="text"
                  v-model="editedFirstName"
                />
                <p v-if="editSaveError" class="help is-danger">{{ editSaveError }}</p>
              </Field>

              <Field>
                <Label>Last Name</Label>
                <input
                  class="stk-input"
                  type="text"
                  v-model="editedLastName"
                />
              </Field>

              <Buttons class="tw-justify-between">
                <!-- make this a true submit button -->
                <Button
                  type="submit"
                  variant="success"
                  :disabled="!editedFirstName"
                >
                  Save Changes
                </Button>
                <Button @click="cancelEditModal" type="button">Cancel</Button>
              </Buttons>
            </Stack>
          </form>
        </StakeModal>
      </template>

      <Callout
        v-else
        status="danger"
      >
        You do not have permission to view this entity.
      </Callout>
    </template>

    <!-- Chat Panel (initially hidden) -->
  </div>
</template>

<script>
import MeetingPrep from './MeetingPrep.vue';
import DocMasterAdvisor from './DocMasterViewAdvisor.vue';
import Dossier from './Dossier.vue';
import PermissionsGroups from './PermissionsGroups.vue';
import EntityPermissions from './EntityPermissions.vue';
import EntityReports from './EntityReports.vue';
import NoteTaking from './NoteTaking.vue';
import OAuthIntegrationDashboard from '@frontend/ui/features/oauth/views/OAuthIntegrationDashboard.vue';
import EntityChat from './EntityChat.vue';
import EntityHoldings from './EntityHoldings.vue';
import EntityRecommendations from './EntityRecommendations.vue';
import EntityRecommendationsConsiderations from './EntityRecommendationsConsiderations.vue';
import EntityPlanning from './planning/EntityPlanning.vue';
import {
  Badge,
  Badges,
  Buttons,
  Button,
  Callout,
  Field,
  Label,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Pills,
  PillsList,
  PillItem,
  Stack,
  StakeModal,
  Subtitle,
  Title,
} from '@stakeui';
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';

export default {
  name: 'EntityDetail',
  components: {
    MeetingPrep,
    DocMasterAdvisor,
    Dossier,
    EntityChat,
    PermissionsGroups,
    EntityPermissions,
    EntityReports,
    NoteTaking,
    EntityHoldings,
    EntityRecommendations,
    EntityRecommendationsConsiderations,
    EntityPlanning,
    OAuthIntegrationDashboard,
    Badge,
    Badges,
    Buttons,
    Button,
    Callout,
    Field,
    Label,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    Pills,
    PillsList,
    PillItem,
    Stack,
    StakeModal,
    Subtitle,
    Title,
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      // We're not at the top-level entity route,
      // which means we're directly heading to one of the tabs,
      // so we're just proceeding
      if (vm.$route.name !== 'entity-detail') {
        return;
      }

      // If we are at the top-level route,
      // redirect to the appropriate first tab
      if (vm.advisorStore.featureFlags.includes('Dossier')) {
        vm.$router.replace({ name: 'entity-dossier' });
      } else {
        vm.$router.replace({ name: 'entity-portfolio' });
      }
    })
  },
  beforeRouteUpdate(to, from, next) {
    this.showCrmLinkingModal = false;
    this.showPortalDrawer = false;
    next();
  },
  beforeRouteLeave(to, from, next) {
    // Clear portfolios before leaving Entity Detail page
    this.checkupStore.portfolios = [];
    this.checkupStore.resetState();

    next(); // Continue to desired route
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    meetingId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      entity: null,
      loading: true,
      showCrmLinkingModal: false,
      showEditModal: false,
      showInviteModal: false,
      showPortalDrawer: false,
      user_permissions: [],
      editedFirstName: '',
      editedLastName: '',
      editSaveError: '',
      portfolioRiskScore: null,
      isFetchingPortfolioRisk: false,
      showChat: false,
      portalStatus: null,
    };
  },
  computed: {
    ...mapStores(useAdvisorStore, useCheckupStore),
    navigationLinks() {
      const items = [
        {
          position: 20,
          label: "Portfolio",
          icon: "fas fa-chart-pie",
          to: { name: "entity-portfolio" },
        },
        {
          position: 30,
          label: "Reports & Preferences",
          icon: "fas fa-file-alt",
          to: { name: "entity-reports" },
        },
        {
          position: 70,
          label: "Groups",
          icon: "fas fa-users",
          to: { name: "entity-groups" },
        },
        // {
        //   position: 80,
        //   label: "Permissions",
        //   icon: "fas fa-lock",
        //   to: { name: "entity-permissions" },
        // },
      ];

      if (this.advisorStore.featureFlags.includes('Dossier')) {
        items.push(
          {
            position: 10,
            label: "Dossier",
            icon: "fas fa-book",
            to: { name: "entity-dossier" },
          },
          {
            position: 40,
            label: "Documents",
            icon: "fas fa-folder-open",
            to: { name: "entity-documents" },
          },
          {
            position: 50,
            label: "Recommendations",
            icon: "fas fa-star",
            to: { name: "entity-recommendations" },
          },
        )
      }
      if (this.advisorStore.featureFlags.includes('Entity Planning')) {
        items.push(
          {
            position: 60,
            label: "Planning",
            icon: "fas fa-chart-line",
            to: { name: "entity-planning" },
          }
        );
      }

      if (this.advisorStore.featureFlags.includes('Meetings')) {
        items.push(
          {
            position: 50,
            label: "Meetings",
            icon: "fas fa-align-left",
            to: { name: "entity-note-taking" },
          }
        );
      }

      items.push(
        {
          position: 50,
          label: "Notes",
          icon: "fas fa-sticky-note",
          to: { name: "entity-simple-notes" },
        }
      );

      return items.sort((a, b) => a.position - b.position);;
    },
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    canViewEntity() {
      return this.user_permissions.includes('view_entity');
    },
    canChangeEntity() {
      return this.user_permissions.includes('change_entity');
    },
    hasPermission() {
      return this.canViewEntity || this.canChangeEntity;
    },
    clientRiskScore() {
      return this.entity?.client_risk_score ?? null;
    },
    clientValues() {
      return this.entity?.screen_sets?.map(s => s.name) || [];
    },
    truncatedValues() {
      return this.clientValues.slice(0, 2);
    },
    extraValuesCount() {
      return Math.max(0, this.clientValues.length - 2);
    },
    portfolioValuesAlignment() {
      return this.entity?.values_alignment_score ?? null;
    },
    portalStatusBadge() {
      if (!this.entity || !this.entity.portal_status) {
        return {
          status: 'info',
          text: 'Loading...'
        };
      }
      
      switch (this.entity.portal_status) {
        case 'not_invited':
          return {
            status: 'warning',
            text: 'Not Invited'
          };
        case 'invited_pending':
          return {
            status: 'warning',
            text: 'Pending Activation'
          };
        case 'activated':
          return {
            status: 'success',
            text: 'Active'
          };
        default:
          return {
            status: 'warning',
            text: 'Not Invited'
          };
      }
    },
  },
  async mounted() {
    await this.fetchEntity();
    this.loading = false;
    await this.fetchPortfolioRiskScore();
    
    // Show drawer by default for advisors with proper permissions and feature flags
    this.showPortalDrawer = this.hasPermission && 
                           this.advisorStore.featureFlags.includes('Dossier') && 
                           this.advisorStore.featureFlags.includes('ClientPortal');
  },
  methods: {
    async fetchMeetings() {
      try {
        if (this.meetingId) {
          return;
        }
        const response = await fetch(`/api/v1/meetings/entity/${this.$route.params.entityExternalId}/`);
        const data = await response.json();
        if (data.meetings && data.meetings.length > 0) {
          // Set the most recent meeting ID
          this.meetingId = data.meetings[0].id;
        } else {
          this.meetingId = 'new';
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      }
    },
    async fetchPortfolioRiskScore() {
      this.isFetchingPortfolioRisk = true;
      try {
        const res = await fetch(`/api/v1/get-entity-portfolio-risk-score/${this.entityExternalId}/`)
        const json = await res.json();
        this.portfolioRiskScore = json.risk_score;
      } catch (err) {
        console.error('Failed loading portfolio risk:', err);
        this.portfolioRiskScore = null;
      } finally {
        this.isFetchingPortfolioRisk = false;
      }
    },

    async fetchEntity() {
      const entityResponse = await fetch(`/api/v1/entities/${this.$route.params.entityExternalId}/`);
      const entityData = await entityResponse.json();
      this.user_permissions = entityData.user_permissions;
      this.entity = entityData;
    },
    async refetchEntityAndRisk() {
      // refresh whether the entity has holdings boolean
      // After holdings are populated, fetch the portfolio risk score
      await this.fetchEntity();
      await this.fetchPortfolioRiskScore();
    },
    async handleCrmContactLinked(data) {
      // Update the entity data after linking
      await this.fetchEntity();
    },
    async handleInvitationSent(email) {
      // Optionally refresh the entity data after invitation is sent
      await this.fetchEntity();
      
      // Show a toast notification or some feedback
      this.$toast.success(`Invitation sent to ${email}`);
      
      // Close the modal
      this.showInviteModal = false;
    },
    async handleInvitationLinkGenerated(data) {
      // Update the entity to reflect portal access
      await this.fetchEntity();
      // Keep the modal open so user can see and copy the link
    },
    openInviteModal() {
      this.showInviteModal = true;
    },
    riskMapping(score) {
      const mappings = {
        '0-20':   { level: 'Conservative',            description: 'You prefer to keep your investments safe, even if it means potentially lower returns.' },
        '21-40':  { level: 'Moderately Conservative', description: 'You are willing to take on a little risk for some potential reward.' },
        '41-60':  { level: 'Moderate',                 description: 'You are comfortable with a moderate level of risk for moderate potential returns.' },
        '61-80':  { level: 'Moderately Aggressive',    description: 'You are willing to take on significant risk for higher potential returns.' },
        '81-100': { level: 'Aggressive',               description: 'You are willing to take on more risk for a potential higher reward, and you\'re okay with the volatility that comes with it.' },
      };

      for (const range in mappings) {
        const [min, max] = range.split('-').map(Number);
        if (score >= min && score <= max) {
          return mappings[range];
        }
      }

      return {
        level: 'Unknown',
        description: 'Risk score is not within expected range.'
      };
    },
    openEditModal() {
      const full = this.entity.name || '';
      const idx = full.lastIndexOf(' ');
      if (idx > 0) {
        this.editedFirstName = full.slice(0, idx);
        this.editedLastName  = full.slice(idx + 1);
      } else {
        // no space → put it all in first name
        this.editedFirstName = full;
        this.editedLastName  = '';
      }
      this.editSaveError = '';
      this.showEditModal  = true;
    },
    cancelEditModal() {
      this.showEditModal = false;
    },
    async saveEntityDetails() {
      // 1) validation
      if (!this.editedFirstName) {
        this.editSaveError = 'First name is required.'
        return
      }

      try {
        // 2) call your update-entity-field endpoint
        const url = `/api/v1/entities/${this.entityExternalId}/update-entity-field/`
        const res = await fetch(url, {
          method: 'POST',
          headers: { 
            ...this.headers, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            first_name: this.editedFirstName,
            last_name:  this.editedLastName
          })
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.error || 'Update failed.')
        }

        // 3) reflect the change immediately
        this.entity.name   = this.editedLastName
          ? `${this.editedFirstName} ${this.editedLastName}`
          : this.editedFirstName
        this.showEditModal = false

      } catch (e) {
        console.error('Error saving entity details:', e)
        this.editSaveError = e.message || 'Failed to save changes.'
      }
    },
    redirectToClientPortal() {
      const advisor = this.advisorStore.advisor;
      const firmSlug = advisor.firm.slug;
      const advisorSlug = advisor.slug;
      //const clientPortalUrl = `/ui/portal/${firmSlug}/${advisorSlug}/${this.entityExternalId}`;
      if (advisor && advisor.slug && advisor.firm && advisor.firm.slug) {
        const firmSlug = advisor.firm.slug;
        const advisorSlug = advisor.slug;
        const clientPortalUrl = `/ui/advisor/client-portal/dashboard/${firmSlug}/${advisorSlug}/${this.entityExternalId}`
        window.open(clientPortalUrl, '_blank');
      } else {
        console.error('Advisor or firm information is not available to construct the client portal URL. The firm object is missing the "slug" property.');
        console.log('Dumping advisorStore.advisor object for debugging:', this.advisorStore.advisor);
        console.log('Dumping advisorStore.advisor object for debugging:', this.advisorStore.advisor.firm);
        alert('Could not open client portal. Required firm information is missing. Please contact support.');
      }
    },
    togglePortalDrawer() {
      this.showPortalDrawer = !this.showPortalDrawer;
    },
    toggleChat() {
      this.showChat = !this.showChat;
    }
  }
};
</script>

<style scoped>
.component {
  background-color: var(--stk-scheme-main);
}

.go-back {
  color: var(--stk-text-weak);
  position: absolute;
  left: 3rem;
  top: 1rem;

  &:hover {
    color: var(--stk-primary);
  }
}

.email {
  color: var(--stk-primary);
}

.modal-content {
  background-color: #fff;
  width: 90%;
  max-width: 60rem;
  max-height: 90vh;
  border-radius: 1em;
  overflow: auto;
}

.portal-status-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.portal-action-section {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  } 
.floating-chat-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.chat-button {
  border-radius: 2rem;
  padding: 0.75rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.chat-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.chat-button.active {
  background-color: #e74c3c;
}

.chat-panel {
  position: fixed;
  bottom: 6rem;
  right: 2rem;
  width: 400px;
  height: 500px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: flex;
  flex-direction: column;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
}

.close-chat-btn {
  padding: 0.5rem;
  min-width: unset;
}

.chat-content {
  flex: 1;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-placeholder {
  color: #666;
  font-style: italic;
}
</style>

